import {
  Action,
  combineReducers,
  configureStore,
  createListenerMiddleware,
  isAnyOf,
  isRejected,
  ThunkAction,
  Tuple,
} from '@reduxjs/toolkit';
import { type Middleware, type UnknownAction } from '@reduxjs/toolkit';
import { hereMapsApi } from './services/hereMaps';
import { microsoftApi } from './services/microsoft';
import { solutionApi } from './services/solution';
import authReducer, { setAccessDenied } from '@/features/Auth/auth-slice';
import globalLoadingReducer from '@/features/GlobalLoading/global-loading-slice';
import logError from '@/common/utils/log-error';
import redactUrlQueryStrings from '@/common/utils/redact-url-query-strings';

const rejectedMatchers = Object.values(solutionApi.endpoints).map((endpoint) => endpoint.matchRejected);

const listenerMiddleware = createListenerMiddleware();

interface ActionWithResponseStatus extends UnknownAction {
  meta?: {
    baseQueryMeta?: {
      response?: {
        status?: number;
      };
    };
  };
}

interface RejectedActionWithRequestMeta extends UnknownAction {
  payload?: {
    error?: string;
  };
  meta?: {
    baseQueryMeta?: {
      request?: {
        method?: string;
        url?: string;
      };
    };
  };
}

const getResponseStatus = (action: UnknownAction): number | undefined => {
  return (action as ActionWithResponseStatus).meta?.baseQueryMeta?.response?.status;
};

const getRequestMeta = (
  action: UnknownAction,
): {
  error: string | undefined;
  method: string | undefined;
  url: string | undefined;
} => {
  const typedAction = action as RejectedActionWithRequestMeta;

  return {
    error: typedAction.payload?.error,
    method: typedAction.meta?.baseQueryMeta?.request?.method,
    url: typedAction.meta?.baseQueryMeta?.request?.url,
  };
};

listenerMiddleware.startListening({
  matcher: isAnyOf(...rejectedMatchers),
  effect: (action, listenerApi): void => {
    const status = getResponseStatus(action);

    if (status === 403) {
      listenerApi.dispatch(setAccessDenied({ accessDenied: true }));
    }
  },
});

const rootReducer = combineReducers({
  auth: authReducer,
  globalLoading: globalLoadingReducer,
  [solutionApi.reducerPath]: solutionApi.reducer,
  [hereMapsApi.reducerPath]: hereMapsApi.reducer,
  [microsoftApi.reducerPath]: microsoftApi.reducer,
});

export type RootState = ReturnType<typeof rootReducer>;

const logRejectedRequest = (action: unknown): void => {
  if (!isRejected(action)) {
    return;
  }

  const { error, method, url } = getRequestMeta(action);

  if (error && method && url) {
    logError(`${error}: ${method} ${redactUrlQueryStrings(url)}`);
  }
};

const createRtkQueryErrorLogger =
  (next: (action: unknown) => unknown) =>
  (action: unknown): unknown => {
    logRejectedRequest(action);

    return next(action);
  };

const rtkQueryErrorLogger: Middleware<object, RootState> = () => createRtkQueryErrorLogger;

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    new Tuple(
      ...getDefaultMiddleware().prepend(listenerMiddleware.middleware),
      solutionApi.middleware,
      hereMapsApi.middleware,
      microsoftApi.middleware,
      rtkQueryErrorLogger,
    ),
});

export type AppDispatch = typeof store.dispatch;
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>;
